import type { ItemStoreState } from "@editor/items/ItemStore";
import type { Migration } from ".";
import type { StoreItem } from "@editor/items/StoreItem";

const migration: Migration = {
  applyToVersions: "<3.0.0-alpha.1",
  type: "before",
  name: "transfer elementMomentums to momentums",
  migrate: (data: {
    elementMomentums?: ItemStoreState<OldMomentumItem>;
    momentums?: ItemStoreState<NewMomentumItem>;
    simulations?: ItemStoreState<SimulationItem>;
    features?: { elementMomentum?: boolean, momentum?: boolean }
  }) => {
    if (data.features && "elementMomentum" in data.features) {
      data.features.momentum = data.features.elementMomentum;
      delete data.features.elementMomentum;
    }

    // Move the old "elementMomentums" store to the new "momentums" store
    if (data.elementMomentums) {
      const defaultNewMomentumStructure: NewMomentumItem = {
        type: "momentum",
        id: -1,
        uid: "momentum_-1",
        description: "",
        elementType: "target",
        elementId: 0,
        interval: 0,
        curveType: "linear",
        curvePoints: [],
        formula: "",
        docs: {},
        createdAt: 0
      };

      if (!data.momentums) {
        data.momentums = {
          items: {},
          order: [],
          count: 0,
          type: "objectstore",
          itemStructure: defaultNewMomentumStructure
        };
      }

      data.momentums.order = [...data.elementMomentums.order];
      data.momentums.count = data.elementMomentums.count;

      for (const item of Object.values(data.elementMomentums.items)) {
        const newItem: NewMomentumItem = { ...defaultNewMomentumStructure };
        for (const key in defaultNewMomentumStructure) {
          if (key in item) {
            transferKey(item, key as (keyof OldMomentumItem & keyof NewMomentumItem), newItem);
          }
        }
        newItem.type = "momentum";
        newItem.uid = `momentum_${item.id}`;
        newItem.elementType = "target";

        data.momentums.items[newItem.id] = newItem;
      }

      delete data.elementMomentums;

      if (data.simulations) {
        // 3. migrate all impulses attributed to old elementMomentums
        for (const simulation of Object.values(data.simulations?.items ?? {})) {
          for (const round of simulation.rounds) {
            for (const impulse of Object.values(round.impulses?.element || {})) {
              for (const sourceValue of impulse.sourceValues) {
                if (sourceValue.type == "elementMomentum") {
                  sourceValue.type = "momentum";
                }
              }
            }
            for (const impulse of Object.values(round.impulses?.resource || {})) {
              for (const sourceValue of impulse.sourceValues) {
                if (sourceValue.type == "elementMomentum") {
                  sourceValue.type = "momentum";
                }
              }
            }
          }
        }
      }
    }

    return data;
  }
};

function transferKey<S extends object, K extends keyof S & keyof O, O extends object>(source: S, key: K, target: O) {
  Object.assign(target, { [key]: source[key] });
}


interface OldMomentumItem extends StoreItem {
  type: "elementMomentum";
  id: number;

  description: string;
  
  elementId: number;

  imageId?: number | null;

  updateType: "constant";

  interval: number;

  curveType: CurveFormType;
  curvePoints: GraphPoint[];

  formula: string;

  order: number;
  docs: Record<string, string>;
}

interface NewMomentumItem extends StoreItem {
  type: "momentum";
  id: number;

  description: string;
  
  elementType: "target" | "resource";
  elementId: number;

  imageId?: number | null;

  interval: number;

  curveType: CurveFormType;
  curvePoints: GraphPoint[];

  formula: string;

  docs: Record<string, string>;
};

interface Point {
  x: number;
  y: number;
}

type CurveFormType =
  | "linear"
  | "custom"
  | "s"
  | "saturation"
  | "exponential"
  | "plateau"
  | "gauss"
  | "formula";

interface GraphPoint extends Point {
  id?: number;
  bezierIn?: Point;
  bezierOut?: Point;
}


type SimulationItem = {
  id: number;
  uid: string;
  type: "simulation";
  createdAt: number;

  title: string;
  description: string;

  rounds: SimulationRoundItem[];
};

type SimulationRoundItem = {
  index: number;

  impulses: {
    element: {
      [elementId: number]: ImpulseItem;
    };
    resource: {
      [resourceId: number]: ImpulseItem;
    };
  };
};

type ImpulseItem = {
  type?: string;

  pointsToAdd: number;
  valueToSet?: number | null;
  valueBefore?: number;
  valueAfter?: number;

  sourceValues: {
    id: number | string;
    type: string;
    value: number;
    randomValue?: number;
    effectId?: number | string;
    effectType: "cost" | "effect" | "net";
  }[];
}

export default migration;
